"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PhoneInput from "@/components/PhoneInput";
import CityPicker from "@/components/CityPicker";
import { SuggestInput } from "@/components/ui/SuggestInput";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

// Модалка «Обратная связь» — 1:1 со Солнышко (CallbackModal/Form), акцент синий.
// Открывается событием window 'open-feedback'. Заявка → /api/v1/feedback →
// Telegram чата ВЫБРАННОГО города (как на Солнышко лид шёл в TG города).
const ACCENT = "#0039A6";

type CityOpt = { station: string; name: string };

function Consent({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "#4A4A50", lineHeight: 1.4 }}>
      <button type="button" aria-pressed={checked} onClick={onToggle} style={{
        marginTop: 1, width: 18, height: 18, flexShrink: 0, cursor: "pointer", borderRadius: 5,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: checked ? `1px solid ${ACCENT}` : "1px solid #C9D3E6", background: checked ? ACCENT : "#fff",
      }}>
        {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </button>
      <span onClick={onToggle} style={{ cursor: "pointer" }}>{children}</span>
    </div>
  );
}

function FeedbackForm({ cities, defaultStation, onSuccess }: { cities: CityOpt[]; defaultStation: string; onSuccess: () => void }) {
  const { t } = useLocale();
  const toast = useToast();
  const [station, setStation] = useState(defaultStation);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [sending, setSending] = useState(false);

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 16px", border: "1px solid #E0E0E5",
    borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", background: "#fff",
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!agreePrivacy || !agreePersonal) { toast.show("Требуется согласие с политикой и обработкой персональных данных", "error"); return; }
    if (!name.trim() || !phone.trim()) { toast.show(t("form.fillNamePhone"), "error"); return; }
    if (!station) { toast.show(t("form.selectCity"), "error"); return; }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.show(t("form.invalidEmail"), "error"); return; }
    setSending(true);
    try {
      const fullPhone = `${dial} ${phone}`.trim();
      const res = await fetch("/api/v1/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ station, place: "Обратная связь (сайт)", clientName: name, clientPhone: fullPhone, clientEmail: email, comment: message, site: "autopilot" }),
      });
      if (!res.ok) throw new Error("Failed");
      track("feedback_submit", { city: station });
      toast.show("Ваша заявка получена, менеджер свяжется с Вами в ближайшее время", "success");
      setName(""); setPhone(""); setEmail(""); setMessage(""); setAgreePrivacy(false); setAgreePersonal(false);
      setTimeout(onSuccess, 600);
    } catch {
      toast.show(t("form.sendError"), "error");
    } finally {
      setSending(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {cities.length > 1 && (
        <CityPicker cities={cities} station={station} onPick={(st) => setStation(st)} inputStyle={inputStyle} />
      )}
      <SuggestInput kind="fio" value={name} onChange={setName} filter={(raw) => raw.replace(/[^A-Za-zА-Яа-яЁё\s\-.]/g, "")} placeholder={`${t("form.name")} *`} inputStyle={inputStyle} />
      <div style={{ height: 48, display: "flex", alignItems: "center", border: "1px solid #E0E0E5", borderRadius: 10, padding: "0 6px" }}>
        <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setDial(d); }} flat required />
      </div>
      <SuggestInput kind="email" value={email} onChange={setEmail} placeholder={t("form.email")} inputStyle={inputStyle} />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("form.message")} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
      <Consent checked={agreePrivacy} onToggle={() => setAgreePrivacy(!agreePrivacy)}>
        {t("consent.privacyPrefix")}{" "}
        <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.privacy")}</a>
      </Consent>
      <Consent checked={agreePersonal} onToggle={() => setAgreePersonal(!agreePersonal)}>
        {t("consent.personalPrefix")}{" "}
        <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.personalData")}</a>
      </Consent>
      <button type="submit" disabled={sending} style={{
        marginTop: 4, height: 50, borderRadius: 10, border: "none", background: ACCENT, color: "#fff",
        fontSize: 16, fontWeight: 700, cursor: sending ? "default" : "pointer", opacity: sending ? 0.6 : 1, fontFamily: "inherit",
      }}>
        {sending ? t("form.sending") : t("form.send")}
      </button>
    </form>
  );
}

export default function FeedbackModal({ cities, defaultStation }: { cities: CityOpt[]; defaultStation: string }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-feedback", onOpen);
    return () => window.removeEventListener("open-feedback", onOpen);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  if (!mounted || !open) return null;

  return createPortal(
    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(13,20,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999, padding: 20, backdropFilter: "blur(4px)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, padding: "32px 32px 28px", maxWidth: 440, width: "100%", position: "relative", boxShadow: "0 20px 60px rgba(13,20,42,0.3)" }}>
        <button onClick={() => setOpen(false)} aria-label="Закрыть" style={{ position: "absolute", top: 16, right: 16, width: 32, height: 32, borderRadius: "50%", border: "none", background: "#F5F5F7", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1E1E2D", margin: "0 0 6px 0" }}>{t("feedback.title")}</h2>
        <p style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>Оставьте контакты — менеджер свяжется с вами в ближайшее время</p>
        <ToastProvider>
          <FeedbackForm cities={cities} defaultStation={defaultStation} onSuccess={() => setOpen(false)} />
        </ToastProvider>
      </div>
    </div>,
    document.body,
  );
}
